import { useRef } from "react";
import {
    motion,
    useReducedMotion,
    useScroll,
    useSpring,
    useTransform,
} from "framer-motion";
import { EASE } from "./Reveal";

const VIDEO_SRC = "/media/moneymac-spin-1080.mp4";
const POSTER = "/media/moneymac-poster.jpg";

function RedWipe() {
    return (
        <motion.div
            aria-hidden="true"
            initial={{ y: 0 }}
            animate={{ y: "-101%" }}
            transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1], delay: 0.15 }}
            className="pointer-events-none fixed inset-0 z-[70] bg-mmm-red"
        />
    );
}

function HeroLine({ children, delay = 0 }) {
    const reduce = useReducedMotion();
    return (
        <span className="block overflow-hidden">
            <motion.span
                className="block"
                initial={reduce ? false : { y: "112%" }}
                animate={{ y: 0 }}
                transition={{ delay, duration: 1, ease: EASE }}
            >
                {children}
            </motion.span>
        </span>
    );
}

export default function Hero() {
    const reduce = useReducedMotion();
    const sectionRef = useRef(null);

    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start start", "end start"],
    });
    const scrollY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
    const scrollScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
    const scrollOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.2]);

    const mx = useSpring(0, { stiffness: 50, damping: 18 });
    const my = useSpring(0, { stiffness: 50, damping: 18 });
    const onMouseMove = (e) => {
        if (reduce) return;
        const r = sectionRef.current?.getBoundingClientRect();
        if (!r) return;
        mx.set(((e.clientX - r.left) / r.width - 0.5) * 18);
        my.set(((e.clientY - r.top) / r.height - 0.5) * 12);
    };

    return (
        <section
            id="top"
            ref={sectionRef}
            data-testid="hero-section"
            onMouseMove={onMouseMove}
            className="relative flex h-[100svh] min-h-[640px] flex-col overflow-hidden bg-black text-white"
        >
            {!reduce && <RedWipe />}

            <div className="relative flex-1">
                <motion.div
                    style={reduce ? {} : { y: scrollY, scale: scrollScale, opacity: scrollOpacity }}
                    className="absolute inset-0 flex items-center justify-center"
                >
                    <motion.div
                        style={reduce ? {} : { x: mx, y: my }}
                        initial={reduce ? false : { opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1.6, ease: EASE, delay: reduce ? 0 : 0.55 }}
                        className="flex h-full w-full items-center justify-center"
                    >
                        {reduce ? (
                            <img
                                data-testid="hero-poster-fallback"
                                src={POSTER}
                                alt="Money Mac Media logo — a glossy red money bag"
                                className="hero-video"
                            />
                        ) : (
                            <video
                                data-testid="hero-logo-video"
                                className="hero-video"
                                src={VIDEO_SRC}
                                poster={POSTER}
                                autoPlay
                                muted
                                loop
                                playsInline
                                preload="metadata"
                                controls={false}
                                disablePictureInPicture
                            />
                        )}
                    </motion.div>
                </motion.div>
            </div>

            <div className="relative px-5 pb-5 md:px-8 md:pb-6">
                <h1
                    data-testid="hero-headline"
                    className="font-display text-[clamp(2.1rem,5vw,5rem)] uppercase leading-[0.9]"
                >
                    <HeroLine delay={reduce ? 0 : 1.0}>It’s time to make you</HeroLine>
                    <HeroLine delay={reduce ? 0 : 1.12}>look money.</HeroLine>
                </h1>

                <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                    <motion.p
                        data-testid="hero-support"
                        initial={reduce ? false : { opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.35, duration: 0.8, ease: EASE }}
                        className="max-w-xl text-sm leading-relaxed text-white/70 md:text-base"
                    >
                        Motion design, animation, 3D, VFX, editing, and Ethical-AI
                        production for brands and shows with something to prove.
                    </motion.p>
                    <motion.p
                        data-testid="hero-accent"
                        initial={reduce ? false : { opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.5, duration: 0.8 }}
                        className="font-display text-3xl uppercase leading-none text-mmm-red md:text-5xl"
                    >
                        Let it rain.
                    </motion.p>
                </div>

                <div className="mt-6 flex items-end justify-between gap-6">
                    <p
                        data-testid="hero-tagline"
                        className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/50 md:text-[11px]"
                    >
                        <HeroLine delay={reduce ? 0 : 1.6}>
                            Your brand called. It wants a raise. — a money mac media
                            company
                        </HeroLine>
                    </p>
                    <motion.p
                        initial={reduce ? false : { opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.7, duration: 0.8 }}
                        className="shrink-0 font-mono text-[10px] uppercase tracking-[0.22em] text-white/40 md:text-[11px]"
                    >
                        Scroll to review ↓
                    </motion.p>
                </div>
            </div>
        </section>
    );
}
